import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';
import { mode } from '@/lib/config/app-mode';

const db = supabase as any;
const enabledNow = () => isSupabaseConfigured() && !mode.isMock;

export interface DataRoomViewStat {
  documentId: string;
  viewerCount: number;
  lastViewedAt?: string;
}

export const dataRoomViewKeys = {
  forStartup: (startupId: string) => ['data-room-view-stats', startupId] as const,
};

/**
 * Founder view: REAL per-document data-room view stats — distinct viewer count
 * and last-viewed timestamp — from the `data_room_events` telemetry via the
 * `get_data_room_view_stats()` aggregate RPC (counts only; never who viewed).
 *
 * Returns a Map keyed by document id. Documents with no recorded views are
 * simply ABSENT from the map — callers treat absent as zero views and show
 * nothing rather than a fabricated number (this replaced the old access-grant /
 * DD-collaborator proxy in `activity.ts`).
 */
export function useDataRoomViewStats(startupId: string | undefined) {
  return useQuery({
    queryKey: dataRoomViewKeys.forStartup(startupId || ''),
    queryFn: async (): Promise<Map<string, DataRoomViewStat>> => {
      if (!startupId || !enabledNow()) return new Map();
      const { data, error } = await rpc('get_data_room_view_stats', { p_startup_id: startupId });
      if (error) throw error;
      const map = new Map<string, DataRoomViewStat>();
      const rows = (data ?? []) as {
        document_id: string;
        viewer_count: number;
        last_viewed_at: string | null;
      }[];
      for (const row of rows) {
        map.set(row.document_id, {
          documentId: row.document_id,
          viewerCount: row.viewer_count ?? 0,
          lastViewedAt: row.last_viewed_at ?? undefined,
        });
      }
      return map;
    },
    enabled: !!startupId,
    staleTime: 60 * 1000,
  });
}
